import { Alert, AlertDescription, Button, Card, CardContent, Spinner } from '@design-system';
import { AlertCircle } from 'lucide-react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  AdvisorPhoneVerificationFlow,
  AdvisorSignupCardHeader,
} from '@client/components/AdvisorPhoneVerificationFlow';
import { useClientSmsMfaEnrollment } from './use-client-sms-mfa';

export function ClientSmsMfaEnrollmentPage() {
  const enrollment = useClientSmsMfaEnrollment();

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="flex h-16 items-center border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pt-18 pb-12 md:pt-20">
        <Card className="bg-background w-full max-w-md gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
          {enrollment.step === 'loading' && (
            <div className="flex min-h-80 items-center justify-center p-10">
              <Spinner className="text-primary size-8" />
            </div>
          )}

          {enrollment.step === 'phone' && (
            <AdvisorPhoneVerificationFlow
              step="phone"
              eyebrow="Verify Your Phone"
              title="Add your phone number"
              description="Enter your phone number to receive a code"
              onSubmit={event => {
                event.preventDefault();
                void enrollment.sendPhoneCode();
              }}
              submitLabel="Send phone code"
              isSubmitting={enrollment.isSubmitting}
              countryCode={enrollment.countryCode}
              phoneNumber={enrollment.phoneNumber}
              onCountryCodeChange={enrollment.updateCountryCode}
              onPhoneNumberChange={enrollment.updatePhoneNumber}
              autoFocus
              showPhoneHelper={false}
              error={enrollment.error ? <ErrorAlert message={enrollment.error} /> : undefined}
            />
          )}

          {enrollment.step === 'verify' && (
            <AdvisorPhoneVerificationFlow
              step="verify"
              eyebrow="Verify Your Phone"
              title="Enter the text code"
              description="We just sent a 6-digit code to your phone."
              onSubmit={event => {
                event.preventDefault();
                void enrollment.verifyPhoneCode();
              }}
              submitLabel="Verify and continue"
              isSubmitting={enrollment.isSubmitting}
              submitDisabled={enrollment.phoneCode.length !== 6}
              phoneNumber={`${enrollment.countryCode} ${enrollment.phoneNumber}`}
              phoneCode={enrollment.phoneCode}
              onPhoneCodeChange={enrollment.setPhoneCode}
              onResend={() => void enrollment.resendPhoneCode()}
              error={enrollment.error ? <ErrorAlert message={enrollment.error} /> : undefined}
              backAction={{
                label: 'Use a different number',
                onClick: enrollment.returnToPhone,
              }}
            />
          )}

          {enrollment.step === 'error' && (
            <>
              <AdvisorSignupCardHeader
                icon={<AlertCircle className="size-5 text-red-600" />}
                eyebrow="Verify Your Phone"
                title="We couldn’t finish setup"
                description="Your personal plan has not been changed."
              />
              <CardContent className="space-y-5 p-6">
                <ErrorAlert message={enrollment.error ?? 'Please try again.'} />
                <Button
                  className="h-12 w-full"
                  onClick={() => void enrollment.retry()}
                  disabled={enrollment.isSubmitting}
                >
                  Try again
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="size-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}
